import { CalendarOptions, EventContentArg, EventInput } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import { Css, Palette } from "@homebound/beam";
import { ReactNode, RefObject, useEffect, useRef, useState } from "react";
import { SchedulingExclusionDatesFragment } from "src/routes/developments/Developments.generated";
import { Temporal } from "temporal-polyfill";
import { DynamicTaskCalendarColorLegend } from "../../schedule-v2/components/TaskColorLegend";
import "./DynamicSchedulesCalendar.css";
import { HolidayEventContent, createHolidayCalendarEvents } from "./dynamicSchedulesCalendarUtils";

type DynamicSchedulesCalendarWrapperProps = Pick<
  CalendarOptions,
  "eventResize" | "eventDrop" | "editable" | "eventClick"
> & {
  events: EventInput[];
  eventContent?: (hookProps: EventContentArg) => ReactNode;
  calendarRef: RefObject<FullCalendar>;
  /* Blocks user interaction while true */
  loading?: boolean;
  schedulingExclusionDates?: SchedulingExclusionDatesFragment[];
};
const baseStyles = { ...Css.h100.$, "--fc-event-resizer-thickness": "18px" };

export function DynamicSchedulesCalendarWrapper(props: DynamicSchedulesCalendarWrapperProps) {
  const {
    calendarRef,
    eventContent,
    events,
    eventDrop,
    eventResize,
    eventClick,
    editable,
    loading,
    schedulingExclusionDates = [],
  } = props;
  const [taskEventScrollTop, setTaskEventScrollTop] = useState<number>();
  const divRef = useRef<HTMLDivElement>(null);

  // Grab scrollPosition of FullCalendar scroller on `eventResize` and `eventDrop` to restore it after re-render
  function setCurrentScrollPosition() {
    const fullCalendarScroller = divRef?.current?.getElementsByClassName("fc-scroller-liquid-absolute")?.[0];
    setTaskEventScrollTop(fullCalendarScroller?.scrollTop);
  }

  useEffect(() => {
    const fullCalendarScroller = divRef?.current?.getElementsByClassName("fc-scroller-liquid-absolute")?.[0];
    if (fullCalendarScroller && taskEventScrollTop) {
      // use a timeout to push this onto the queue
      setTimeout(() => {
        fullCalendarScroller.scrollTop = taskEventScrollTop;
      }, 0);
    }
  }, [events, taskEventScrollTop]);

  return (
    <div
      ref={divRef}
      className="hb-dynamic-schedules-calendar"
      css={{ ...baseStyles, ...Css.if(loading === true).cursor("wait").$ }}
    >
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, ...(editable ? [interactionPlugin] : [])]}
        headerToolbar={{ left: "title,prev,next", center: "today,dayGridWeek,dayGridMonth", end: "" }}
        events={[...createHolidayCalendarEvents(schedulingExclusionDates), ...events]}
        eventClick={eventClick}
        // 100% of height - (pb2 of sibling + 40px max height from inner DynamicTaskColorLegend)
        height="calc(100% - 56px)"
        // Holidays always on top
        eventOrder={(a, b) => {
          // Keep holidays at the top
          if ((a as EventInput)?.extendedProps?.type === "holiday") return -1;

          // Sort all the events by duration (greater duration first)
          const durationA = (a as EventInput)?.extendedProps?.duration;
          const durationB = (b as EventInput)?.extendedProps?.duration;
          if (durationA && durationB) return durationB - durationA;

          return 1;
        }}
        eventOrderStrict={true}
        dayHeaderContent={({ text }) => <div css={Css.sm.gray900.mb1.$}>{text}</div>}
        dayCellClassNames={({ date }) =>
          schedulingExclusionDates.some(({ date: hDate }) => isSameDate(hDate, date)) ? ["hb-fc-holiday-cell"] : []
        }
        dayCellContent={({ dayNumberText }) => <div css={Css.xs.blue700.$}>{dayNumberText}</div>}
        eventContent={eventContent}
        editable={editable && !loading}
        droppable={editable}
        eventResize={(args) => {
          setCurrentScrollPosition();
          eventResize?.(args);
        }}
        eventDrop={(args) => {
          setCurrentScrollPosition();
          eventDrop?.(args);
        }}
        // Note: `view` prop overrides render methods for month and week views
        views={{
          dayGridWeek: {
            // Mimic how google calendar only shows holidays within header for week view
            eventContent: (args) => args.event.extendedProps.type === "planTask" && eventContent?.(args),
            dayHeaderContent: ({ text, date }) => {
              const maybeHoliday = schedulingExclusionDates?.find(({ date: hDate }) => isSameDate(hDate, date));
              if (maybeHoliday) {
                return (
                  <div css={Css.df.fdc.gap1.aic.$}>
                    <div css={Css.sm.gray900.$}>{text}</div>
                    <HolidayEventContent title={maybeHoliday.name} />
                  </div>
                );
              } else return <div css={Css.sm.gray900.mb1.$}>{text}</div>;
            },
          },
        }}
      />
      <div
        // Match bgWhite of table and complete border radius to complete card appearance
        css={Css.bgWhite.pb2.borderRadius("0 0 8px 8px").$}
      >
        <DynamicTaskCalendarColorLegend
          title="Color Legend:"
          items={[
            { color: Palette.Blue500, label: "A day that this task was worked on" },
            { color: Palette.Blue200, label: "Estimated work days / No work done" },
            { color: Palette.Red200, label: "Trade - Not Sent" },
            { color: Palette.Yellow200, label: "Trade - Sent and Unconfirmed" },
            { color: Palette.Gray800, icon: "truck", label: "Material Drop", sizeOverride: 2 },
            { color: Palette.Red600, icon: "flag", label: "Delay Flag", sizeOverride: 2 },
            { color: Palette.Gray800, icon: "hardHatInverse", label: "Trade - Worked", sizeOverride: 2 },
            { color: Palette.Gray800, icon: "hardHat", label: "Trade - Scheduled", sizeOverride: 2 },
          ]}
        />
      </div>
    </div>
  );
}

function isSameDate(plainDate: Temporal.PlainDate, date: Date): boolean {
  return toPlainDate(date).equals(plainDate);
}

function toPlainDate(date: Date): Temporal.PlainDate {
  return Temporal.PlainDate.from({ year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() });
}
